import React, { ReactNode, useState, useMemo, useCallback } from 'react';
import Select, { components, DropdownIndicatorProps } from 'react-select';
import { Box } from '@primer/components';
import Label from 'uikit/labels/Labels/Labels';
import LabelWithEllipsis from 'uikit/labels/LabelWithEllipsis/LabelWithEllipsis';
import { isEmpty } from 'utils/common';
import {
    sortOptionsByLabel,
    TOP_PINNED_DROPDOWN_VALUES,
    BOTTOM_PINNED_DROPDOWN_VALUES,
    isPinnedDropdownOption,
} from 'utils/sortDropdownOptions';
import { ReactComponent as DownIcon } from 'images/actions/down-arrow.svg';
import StatusDropdownArrow from 'images/status-dropdown-arrow.svg';
import CustomOption, { DropdownOptionItem } from './CustomOption/CustomOption';
import DropdownIndicator from './CustomDropdownIndicator/CustomDropdownIndicator';
import CustomValueControl from './CustomValueControl/CustomValueControl';
import VirtualizedMenuList from './VirtualizedMenuList';
import SelectAllMenuList from './SelectAllMenuList';
import './CustomDropdown.scss';
// https://www.saltycrane.com/cheat-sheets/typescript/react-select/latest/#react-select

interface CustomDropdownPropType {
    label?: string;
    isSingleSelect?: boolean;
    options: DropdownOptionItem[];
    setSelected: (arg0: DropdownOptionItem[]) => void;
    selected: DropdownOptionItem[];
    isSingleArrow?: boolean;
    isHideDropdownIndicator?: boolean;
    arrowSize?: number; // Optional size for dropdown arrow icon
    dropdownWidth?: number | string;
    menuWidth?: number | string; // Optional width for the opened menu
    height?: number; // Optional custom control height
    alreadyUsedOption?: DropdownOptionItem[];
    isCopyIconVisible?: boolean;
    isShowErrorMessage?: boolean;
    errorMessage?: string;
    isValueUpdated?: boolean;
    isSearchable?: boolean;
    menuShouldBlockScroll?: boolean;
    tooltipLabel?: string | ReactNode;
    labelClass?: string;
    readOnly?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    onSearchInputChange?: (input: string) => void;
    isSearchOutSide?: boolean;
    placeholder?: string;
    className?: string;
    labelInfoText?: string;
    isRequired?: boolean;
    handleActionClick?: () => void | undefined;
    icon?: string | ReactNode;
    isClearable?: boolean;
    CustomIcon?: string | ReactNode;
    onCustomIconClick?: (value?: string) => void | undefined;
    noOptionsMessage?: string;
    isHandleSearchLocally?: boolean;
    isShowBorder?: boolean;
    onMenuClose?: () => void | undefined;
    isButtonStyle?: boolean; // New prop for status button style
    customLabel?: string;
    menuPortalZIndex?: number; // New prop for menu z-index in modals
    variant?: 'default' | 'horizontal' | 'horizontal-stacked'; // New prop for horizontal layout
    disableHoverTitle?: boolean; // Disable native title tooltip on hover
    useFileIcon?: boolean; // Use file icon instead of search icon
    enableVirtualization?: boolean; // Enable virtualization for large lists (backward compatible)
    isShowSelectAll?: boolean; // Show select all / deselect all for multi-select only
    onBulkEmailInput?: (
        inputValue: string,
        meta?: { isPaste?: boolean }
    ) => boolean; // Return true to clear search input
    sortOptionsAlphabetically?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownPropType> = (props) => {
    const {
        label,
        isSingleSelect = true,
        isSingleArrow = false,
        isHideDropdownIndicator = false,
        arrowSize = 10,
        isClearable = false,
        options,
        selected,
        setSelected,
        dropdownWidth = 262,
        menuWidth,
        height,
        alreadyUsedOption = [],
        isCopyIconVisible = false,
        isShowErrorMessage = false,
        errorMessage = '',
        isValueUpdated = false,
        isSearchable = false,
        menuShouldBlockScroll = false,
        tooltipLabel = '',
        labelClass = 'with-padding',
        readOnly = false,
        isDisabled = false,
        isLoading = false,
        onSearchInputChange,
        isSearchOutSide = false,
        placeholder = 'Select...',
        className = '',
        labelInfoText = '',
        isRequired = false,
        handleActionClick,
        icon = '',
        CustomIcon,
        onCustomIconClick,
        noOptionsMessage = 'No options',
        isHandleSearchLocally = false,
        isShowBorder = false,
        onMenuClose,
        isButtonStyle = false,
        customLabel,
        menuPortalZIndex = 10002,
        variant = 'default',
        disableHoverTitle = false,
        useFileIcon = false,
        enableVirtualization = false,
        isShowSelectAll = false,
        onBulkEmailInput,
        sortOptionsAlphabetically = true,
    } = props;

    const shouldControlSearchInput =
        isHandleSearchLocally ||
        !!onBulkEmailInput ||
        (isSearchable && !isSingleSelect);
    const shouldControlMenu = !!onBulkEmailInput;

    const [isFocused, setIsFocused] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const sortedOptions = useMemo(() => {
        const shouldSortOptions = sortOptionsAlphabetically && !isShowBorder;

        return sortOptionsByLabel(options, {
            sortOptionsAlphabetically: shouldSortOptions,
        });
    }, [options, sortOptionsAlphabetically, isShowBorder]);

    const handleSelectedValue = useCallback(
        (newValue: unknown) => {
            if (isSingleSelect) {
                setSelected([{ ...(newValue as DropdownOptionItem) }]);
            } else {
                setSelected([...(newValue as DropdownOptionItem[])]);
            }
        },
        [isSingleSelect, setSelected]
    );

    const handleInputChange = (
        inputValue: string,
        { action }: { action: string }
    ) => {
        // Only trigger onSearchInputChange when the action is 'input-change'
        // This prevents the callback from being triggered on selection
        if (action === 'input-change') {
            const value = inputValue || '';

            if (onBulkEmailInput?.(value)) {
                setSearchInput('');
                onSearchInputChange?.('');
                return;
            }

            setSearchInput(value);
            onSearchInputChange?.(value);
        }
    };

    const handleBulkEmailPaste = useCallback(
        (event: React.ClipboardEvent<HTMLInputElement>) => {
            if (!onBulkEmailInput) return;

            const pastedText = event.clipboardData?.getData('text') || '';
            if (!pastedText.trim()) return;

            const handled = onBulkEmailInput(pastedText, { isPaste: true });
            if (handled) {
                event.preventDefault();
                setSearchInput('');
                onSearchInputChange?.('');
                setMenuIsOpen(false);
                event.currentTarget.blur();
            }
        },
        [onBulkEmailInput, onSearchInputChange]
    );

    const handleMenuOpen = useCallback(() => {
        setMenuIsOpen(true);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuIsOpen(false);

        if (shouldControlSearchInput) {
            setSearchInput('');
            onSearchInputChange?.('');
        }

        onMenuClose?.();
    }, [shouldControlSearchInput, onMenuClose, onSearchInputChange]);

    // Pinned rows always pass search filter regardless of label text
    const pinnedFilterValues = useMemo(
        () => [...TOP_PINNED_DROPDOWN_VALUES, ...BOTTOM_PINNED_DROPDOWN_VALUES],
        []
    );

    // filterOptions: function to filter dropdown options using search box
    const filterOptions = useCallback(
        (option: unknown, filter: string) => {
            if (!filter || isSearchOutSide) {
                return true;
            }

            const {
                label: optionLabel,
                value: optionValue,
                data,
                email,
            } = option as DropdownOptionItem;
            const optionEmail = email || data?.email;
            const labelText = String(optionLabel ?? '');
            const valueText = String(optionValue ?? '');

            if (
                (optionLabel &&
                    typeof optionLabel === 'string' &&
                    optionLabel
                        .toLowerCase()
                        .includes(filter?.toLowerCase())) ||
                (optionEmail &&
                    optionEmail
                        .toLowerCase()
                        .includes(filter?.toLowerCase())) ||
                pinnedFilterValues.includes(labelText) ||
                pinnedFilterValues.includes(valueText) ||
                isPinnedDropdownOption(option as DropdownOptionItem)
            ) {
                return true;
            }
            return false;
        },
        [isSearchOutSide, pinnedFilterValues]
    );

    const componentsState = useMemo(
        () => ({
            Option: CustomOption,
            DropdownIndicator: (dropdownProps: DropdownIndicatorProps) => {
                if (isHideDropdownIndicator) return null;
                if (isButtonStyle) {
                    return (
                        <components.DropdownIndicator {...dropdownProps}>
                            <Box paddingRight="5px" display="flex">
                                <DownIcon />
                            </Box>
                        </components.DropdownIndicator>
                    );
                }
                return (
                    <DropdownIndicator
                        isSingleArrow={isSingleArrow}
                        arrowSize={arrowSize}
                        {...dropdownProps}
                    />
                );
            },
            IndicatorSeparator: () => null,
            Control: CustomValueControl,
            MultiValue: components.MultiValue,
            ...(isShowSelectAll && !isSingleSelect
                ? { MenuList: SelectAllMenuList }
                : enableVirtualization
                ? { MenuList: VirtualizedMenuList }
                : {}),
            ...(onBulkEmailInput
                ? {
                      Input: (
                          inputProps: React.ComponentProps<
                              typeof components.Input
                          >
                      ) => {
                          const { onPaste, ...restInputProps } = inputProps;

                          return (
                              <components.Input
                                  {...restInputProps}
                                  onPaste={(event) => {
                                      onPaste?.(event);
                                      if (!event.defaultPrevented) {
                                          handleBulkEmailPaste(event);
                                      }
                                  }}
                              />
                          );
                      },
                  }
                : {}),
        }),
        [
            isHideDropdownIndicator,
            isButtonStyle,
            isSingleArrow,
            arrowSize,
            enableVirtualization,
            isShowSelectAll,
            isSingleSelect,
            onBulkEmailInput,
            handleBulkEmailPaste,
        ]
    );

    const placeholderLabel: ReactNode = useMemo(() => {
        if (selected.length === 0) return placeholder;

        // for multi select
        if (!isSingleSelect) {
            return `${selected.length} ${
                customLabel ||
                (selected.length === 1 ? 'item selected' : 'items selected')
            }`;
        }

        if (selected[0]?.label) {
            if (!isShowBorder) {
                // for single select
                return `${selected[0].label}`;
            }

            const isValueValid =
                !isEmpty(selected[0]?.value) && `${selected[0]?.value}` !== '0';
            return isValueValid ? (
                <Box
                    width="100%"
                    borderBottom={`${selected[0]?.value}px solid black`}
                    marginLeft="10px"
                    marginRight="10px"
                    alignSelf="center"
                />
            ) : (
                `${
                    options.find((option) => `${option?.value}` === '0')
                        ?.label ?? options[0]?.label
                }`
            );
        }
        return placeholder;
    }, [
        selected,
        isSingleSelect,
        placeholder,
        customLabel,
        isShowBorder,
        options,
    ]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handleCustomMenuClose = () => {
        if (shouldControlMenu) {
            handleMenuClose();
            return;
        }

        if (shouldControlSearchInput) {
            // Clear search input when menu closes
            setSearchInput('');
            onSearchInputChange?.('');
        }

        onMenuClose?.();
    };

    // Custom styles for menu z-index in modals and optional control height
    const customStyles: any = {};
    if (menuPortalZIndex) {
        customStyles.menuPortal = (base: any) => ({
            ...base,
            zIndex: menuPortalZIndex,
        });
    }
    if (menuWidth) {
        customStyles.menu = (base: any) => ({
            ...base,
            width: typeof menuWidth === 'string' ? menuWidth : `${menuWidth}px`,
        });
    }
    if (height) {
        customStyles.control = (base: any) => ({
            ...base,
            minHeight: `${height}px`,
            height: `${height}px`,
        });
        customStyles.valueContainer = (base: any) => ({
            ...base,
            minHeight: `${height}px`,
        });
        customStyles.indicatorsContainer = (base: any) => ({
            ...base,
            height: `${height}px`,
        });
        customStyles.input = (base: any) => ({
            ...base,
            margin: 0,
            padding: 0,
        });
        customStyles.placeholder = (base: any) => ({
            ...base,
            lineHeight: `${height - 16}px`,
        });
    }

    const isHorizontal = variant === 'horizontal';
    const isHorizontalStacked = variant === 'horizontal-stacked';
    const isStackedLayout = variant === 'default' || isHorizontalStacked;

    return (
        <Box>
            <Box
                id="dataImportDropdown"
                width={isHorizontal ? 'auto' : dropdownWidth}
                display={isStackedLayout ? 'block' : 'flex'}
                justifyContent={isHorizontal ? 'space-between' : 'flex-start'}
                alignItems={isHorizontal ? 'center' : 'flex-start'}
                // gap={isHorizontal ? '35px' : 0}
            >
                {label && (
                    <Box
                        display="flex"
                        justifyContent={
                            isHorizontal ? 'flex-start' : 'space-between'
                        }
                        alignItems="center"
                        flexShrink={isHorizontal ? 0 : 1}
                        minWidth={isHorizontal ? 'fit-content' : 'auto'}
                        marginBottom={isHorizontalStacked ? '8px' : '0'}
                    >
                        <Label
                            variant={
                                !readOnly
                                    ? isHorizontal || isHorizontalStacked
                                        ? 'label-secondary'
                                        : 'normal'
                                    : 'covision-input-label'
                            }
                            labelClass={labelClass}
                            label={label}
                            infoText={labelInfoText}
                        />
                        {variant !== 'horizontal' && (
                            <Box display="flex">
                                {isRequired && !readOnly && (
                                    <Label
                                        label="Required"
                                        variant="outline"
                                        labelClass="required-label"
                                    />
                                )}
                                {icon && (
                                    <Box
                                        paddingLeft="5px"
                                        onClick={handleActionClick}
                                        className="icon-image"
                                    >
                                        {icon}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
                <Box
                    width={
                        variant === 'horizontal'
                            ? typeof dropdownWidth === 'string'
                                ? dropdownWidth
                                : `${dropdownWidth}px`
                            : '100%'
                    }
                >
                    {readOnly ? (
                        <Box>
                            {isSingleSelect ? (
                                <Label
                                    label={selected
                                        .map((item) => item.label)
                                        .join(', ')}
                                    variant="normal"
                                    labelClass="with-padding"
                                />
                            ) : (
                                <LabelWithEllipsis
                                    value={selected
                                        .map((item) => item.label)
                                        .join(', ')}
                                    width={
                                        typeof dropdownWidth === 'number'
                                            ? dropdownWidth
                                            : 262
                                    }
                                    className="covision-input-label"
                                />
                            )}
                        </Box>
                    ) : (
                        <Box
                            title={
                                disableHoverTitle
                                    ? ''
                                    : isSingleSelect &&
                                      placeholder !== placeholderLabel &&
                                      !isShowBorder
                                    ? (placeholderLabel as string)
                                    : ''
                            }
                        >
                            <Select
                                classNamePrefix="custom-dropdown"
                                className={`${className} ${
                                    isShowErrorMessage ? 'error' : ''
                                } ${
                                    isSearchable
                                        ? useFileIcon
                                            ? 'file-icon'
                                            : 'search-icon'
                                        : ''
                                } ${isValueUpdated ? 'updated' : ''} ${
                                    isButtonStyle ? 'status-button-style' : ''
                                } ${
                                    isHorizontal || isHorizontalStacked
                                        ? 'horizontal-style'
                                        : ''
                                }`}
                                menuPortalTarget={document.body}
                                menuShouldBlockScroll={menuShouldBlockScroll}
                                options={sortedOptions}
                                components={componentsState}
                                isSearchable={isSearchable}
                                isDisabled={isDisabled}
                                isLoading={isLoading}
                                value={selected}
                                onChange={handleSelectedValue}
                                closeMenuOnSelect={isSingleSelect}
                                isClearable={isClearable}
                                backspaceRemovesValue={false}
                                controlShouldRenderValue={false} // we are showing value as placeholder so not showing actual value
                                // to pass custom props inside this react-select component we have to disabled ts for props
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                alreadyUsedOption={alreadyUsedOption}
                                menuPlacement="auto"
                                menuPosition="fixed"
                                maxMenuHeight={200}
                                filterOption={filterOptions}
                                tooltipLabel={tooltipLabel}
                                isCopyIconVisible={isCopyIconVisible}
                                CustomIcon={CustomIcon}
                                onCustomIconClick={onCustomIconClick}
                                isMulti={!isSingleSelect}
                                hideSelectedOptions={false}
                                noOptionsMessage={() =>
                                    isSearchOutSide ? null : noOptionsMessage
                                }
                                placeholder={
                                    isSearchable && isFocused
                                        ? ''
                                        : placeholderLabel
                                }
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                blurInputOnSelect={isSingleSelect}
                                isShowBorder={isShowBorder}
                                onMenuClose={handleCustomMenuClose}
                                menuPortalZIndex={menuPortalZIndex}
                                styles={customStyles}
                                enableVirtualization={enableVirtualization}
                                {...(shouldControlSearchInput
                                    ? {
                                          onInputChange: handleInputChange,
                                          inputValue: searchInput,
                                      }
                                    : {
                                          onInputChange: onSearchInputChange,
                                      })}
                                {...(shouldControlMenu
                                    ? {
                                          menuIsOpen,
                                          onMenuOpen: handleMenuOpen,
                                      }
                                    : {})}
                            />
                        </Box>
                    )}
                    {isShowErrorMessage && errorMessage ? (
                        <Label label={errorMessage} variant="error" />
                    ) : (
                        <></>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CustomDropdown;
